/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { IAdminInterview } from 'src/api'

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: row;
`

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 170px;
  margin-right: 10px;
`

const ItemStatus = styled.div`
  font-size: 12px;
  font-weight: 400;
  margin: 8px 4px;
`

const ItemAgencyContainer = styled.div`
  background: #edeef2;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
`

const ItemAgencyName = styled.div`
  font-size: 12px;
  font-weight: 400;
`

const ItemInterviewsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 8px;
`

const InterviewLink = styled.a`
  font-size: 12px;
  width: fit-content;
`

interface IDashboardItem {
  status: DashboardInterviewStatus
  agencies: IAgencyBlock[]
}

interface IInterviewItem {
  id: string
  orderNumber: number
}

interface IAgencyBlock {
  name: string
  id: string
  interviews: IInterviewItem[]
}

enum DashboardInterviewStatus {
  INVITED = 'Invited',
  MATCHING = 'Matching',
  MATCHED = 'Matched',
  SCHEDULED = 'Scheduled',
  ENDED = 'Ended',
  COMPLETED = 'Completed',
  INCOMPLETE = 'Incomplete',
}

interface IProps {
  interviews: IAdminInterview[]
}

const DashboardTableComponent = ({ interviews }: IProps) => {
  const [dashboardItems, setDashboardItems] = useState<IDashboardItem[]>([])

  const getDItemCount = (item: IDashboardItem) => {
    let count = 0
    item.agencies.forEach((a) => {
      count += a.interviews.length
    })
    return count
  }

  const pushInterview = (
    src: IDashboardItem[],
    status: DashboardInterviewStatus,
    interview: any
  ) => {
    const { agency } = interview
    let dItem = src.find((item) => item.status === status)
    if (!dItem) {
      dItem = {
        status,
        agencies: [],
      }
      src.push(dItem)
    }
    let dAgency = dItem.agencies.find((item) => item.id === agency.id)
    if (!dAgency) {
      dAgency = {
        name: agency.internal_name ?? agency.name,
        id: agency.id,
        interviews: [],
      }
      dItem.agencies.push(dAgency)
    }
    dAgency.interviews.push({
      id: interview.id,
      orderNumber: interview.order_number,
    })
  }

  const getStatusOrder = (status: DashboardInterviewStatus) => {
    switch (status) {
      case DashboardInterviewStatus.INVITED:
        return 0
      case DashboardInterviewStatus.MATCHING:
        return 1
      case DashboardInterviewStatus.MATCHED:
        return 2
      case DashboardInterviewStatus.SCHEDULED:
        return 3
      case DashboardInterviewStatus.ENDED:
        return 4
      case DashboardInterviewStatus.COMPLETED:
        return 5
      case DashboardInterviewStatus.INCOMPLETE:
        return 6
      default:
        return -1
    }
  }

  useEffect(() => {
    const newDItems: IDashboardItem[] = []
    interviews.forEach((interview) => {
      // invited
      if (interview.admin_status === 'Invited') {
        pushInterview(newDItems, DashboardInterviewStatus.INVITED, interview)
      }
      // matching
      if (
        interview.admin_status === 'Order form submitted' ||
        interview.admin_status === 'Pending expert availability' ||
        interview.admin_status === 'Expert availability provided'
      ) {
        pushInterview(newDItems, DashboardInterviewStatus.MATCHING, interview)
      }
      // matched
      if (interview.admin_status === 'Matched') {
        pushInterview(newDItems, DashboardInterviewStatus.MATCHED, interview)
      }
      // scheduled
      if (
        interview.admin_status === 'Scheduled' ||
        interview.admin_status === 'Started'
      ) {
        pushInterview(newDItems, DashboardInterviewStatus.SCHEDULED, interview)
      }
      // ended
      if (
        interview.admin_status === 'Pending Report' ||
        interview.admin_status === 'Pending Confirmation'
      ) {
        pushInterview(newDItems, DashboardInterviewStatus.ENDED, interview)
      }
      // completed
      if (interview.admin_status === 'Completed') {
        pushInterview(newDItems, DashboardInterviewStatus.COMPLETED, interview)
      }
      // incomplete
      if (interview.admin_status === 'Incomplete') {
        pushInterview(newDItems, DashboardInterviewStatus.INCOMPLETE, interview)
      }
    })
    newDItems.sort((a, b) =>
      getStatusOrder(a.status) > getStatusOrder(b.status) ? 1 : -1
    )
    setDashboardItems(newDItems)
  }, [interviews])

  return (
    <ItemsContainer>
      {dashboardItems.map((dItem, index) => (
        <ItemContainer key={index}>
          <ItemStatus>{`${dItem.status} - ${getDItemCount(dItem)}`}</ItemStatus>
          {dItem.agencies.map((dAgency) => (
            <ItemAgencyContainer key={dAgency.id}>
              <ItemAgencyName>{`${dAgency.name} (${dAgency.interviews.length})`}</ItemAgencyName>
              <ItemInterviewsContainer>
                {dAgency.interviews.map((interview) => (
                  <InterviewLink
                    href={`/agencies/${dAgency.id}/edit/${interview.id}`}
                    target="_blank"
                  >
                    {interview.orderNumber}
                  </InterviewLink>
                ))}
              </ItemInterviewsContainer>
            </ItemAgencyContainer>
          ))}
        </ItemContainer>
      ))}
    </ItemsContainer>
  )
}

export default DashboardTableComponent
